import { DateTime } from "luxon";
import { type ActionType } from "../../common/components/ActionButton";
import { DEFAULT_BASE_MONEY_PROPS } from "../Money/constants";
import { MoneyType } from "../Money/types";
import { getTotalMonies } from "../Money/utils";
import { subtractMoney } from "../Wallet/utils";
import { AccountingPayableNode, TransactionRowItem } from "./types";

/**
 * Determines the action type for a transaction row based on:
 * - If there are payment batches requiring treasury review → "review"
 * - Else if remaining to pay > 0 → "pay"
 * - Otherwise → null (no action)
 *
 * @param hasPaymentBatchesForReview - Whether there are payment batches needing review
 * @param remainingToPayAmount - The remaining amount to pay
 */
export const determineActionType = (
  hasPaymentBatchesForReview: boolean,
  remainingToPayAmount: MoneyType,
): ActionType => {
  if (hasPaymentBatchesForReview) {
    return "review";
  }
  if (remainingToPayAmount.amount > 0) {
    return "pay";
  }
  return null;
};

/**
 * Calculates the remaining amount to pay for an accounting payable.
 *
 * Remaining = totalAmountPayable − amountMarkedPaid − amountPaidViaAutomatedPayments
 *
 * @param node - Raw GQL node from GetAccountingPayablesQuery
 */
export const calculateRemainingToPayAmount = (node: AccountingPayableNode): MoneyType => {
  let remaining: MoneyType[] = node.totalAmountPayable?.monies ?? [];

  if (node.amountMarkedPaid?.monies?.length) {
    remaining = subtractMoney(remaining, node.amountMarkedPaid.monies);
  }

  if (node.amountPaidViaAutomatedPayments) {
    remaining = subtractMoney(remaining, [node.amountPaidViaAutomatedPayments]);
  }

  return getTotalMonies(remaining) ?? DEFAULT_BASE_MONEY_PROPS;
};

/**
 * Builds the description shown in the Description column.
 * When the transaction is for a single position, prepends the investor (position
 * account) name separated by a middle dot, e.g. "Geoffrey Bergnaum · BSV Q1 Redemption".
 *
 * @param node - Raw GQL node from GetAccountingPayablesQuery
 */
export const getTransactionDescription = (node: AccountingPayableNode): string => {
  const description = node.description ?? "";
  if (node.payeeCount !== 1) return description;
  const investorName = node.payees?.[0]?.position?.account?.name;
  // Show the investor name when there is no description, rather than dropping it
  // and rendering an empty cell.
  if (!investorName) return description;
  if (!description) return investorName;
  return `${investorName} · ${description}`;
};

/**
 * Transforms a raw GQL AccountingPayable node into a TransactionRowItem for AG Grid.
 *
 * @param node - Raw GQL node from GetAccountingPayablesQuery
 */
export const toTransactionRowItem = (node: AccountingPayableNode): TransactionRowItem => {
  const totalAmountPayable =
    getTotalMonies(node.totalAmountPayable?.monies) ?? DEFAULT_BASE_MONEY_PROPS;

  const amountPaidViaAutomatedPayments =
    node.amountPaidViaAutomatedPayments ?? DEFAULT_BASE_MONEY_PROPS;

  const remainingToPayAmount = calculateRemainingToPayAmount(node);

  // paymentBatches is pre-filtered by READY_FOR_TREASURY_REVIEW in the GQL query,
  // so any results indicate a payment batch needs treasury review.
  const hasPaymentBatchesForReview = (node.paymentBatches?.length ?? 0) > 0;
  const action = determineActionType(hasPaymentBatchesForReview, remainingToPayAmount);
  const paymentBatchesForReview = (node.paymentBatches ?? []).map((pb) => pb.id);

  return {
    id: node.id,
    objectType: node.objectType,
    label: node.label ?? "",
    subtitleLabel:
      node.label?.toLowerCase() === "negative contribution" ? "" : node.subtitleLabel ?? "",
    description: getTransactionDescription(node),
    date: node.date ? DateTime.fromISO(node.date) : DateTime.invalid("missing"),
    requestedPaymentDate: node.requestedPaymentDate
      ? DateTime.fromISO(node.requestedPaymentDate)
      : null,
    clientName: node.arena?.name ?? "",
    entityName: node.entity?.name ?? "",
    payeeCount: node.payeeCount ?? 0,
    totalAmountPayable,
    amountPaidViaAutomatedPayments,
    remainingToPayAmount,
    isPaymentRequested: node.isPaymentRequested ?? false,
    action,
    paymentBatchesForReview,
  };
};
